<script lang="ts">
	import { resolve } from '$app/paths';
	import ArrowIcon from '$lib/components/marketing/redesign2025/ArrowIcon.svelte';
	import PageHero from '$lib/components/marketing/redesign2025/PageHero.svelte';
	import { m } from '$lib/paraglide/messages.js';
	import { getLocale, localizeHref } from '@/paraglide/runtime';
	import Phone from '@lucide/svelte/icons/phone';

	const supportPhone = '+34679855762';
	const supportPhoneDisplay = '+34 679 855 762';

	const locale = $derived(getLocale());

	const features = $derived.by(() => {
		void locale;
		return [
			{
				n: '01',
				title: m.l7_feat1_title(),
				body: m.l7_feat1_body()
			},
			{
				n: '02',
				title: m.l7_feat2_title(),
				body: m.l7_feat2_body()
			},
			{
				n: '03',
				title: m.l7_feat3_title(),
				body: m.l7_feat3_body()
			}
		];
	});
</script>

<PageHero
	eyebrow={m.l7_hero_eyebrow()}
	breadcrumbLabel={m.l7_hero_breadcrumb()}
	lead={m.l7_hero_lead()}
>
	{#snippet heroImage(className)}
		<enhanced:img
			src="/static/img/landing/redesign-2025/photos/rafted-turquoise.avif"
			alt=""
			class={className}
			fetchpriority="high"
			sizes="100vw"
		></enhanced:img>
	{/snippet}
	{m.l7_hero_h1_before()}<em class="italic">{m.l7_hero_h1_em()}</em>
</PageHero>

<section class="py-20 md:py-[100px]">
	<div class="mx-auto max-w-[1240px] px-5 md:px-10">
		<div class="grid items-start gap-9 lg:grid-cols-[0.9fr_1.1fr] lg:gap-[70px]">
			<div>
				<span
					class="mb-5 block text-[0.72rem] font-semibold tracking-[0.22em] text-zp-muted uppercase"
					>{m.l7_law_eyebrow()}</span
				>
				<h2 class="text-[clamp(1.9rem,3.4vw,3rem)] leading-[1.06]">
					{m.l7_law_h2()}
				</h2>
			</div>
			<div>
				<p class="mb-5 text-[1.1rem] text-zp-prose">
					{m.l7_law_p1()}
				</p>
				<p class="mb-0 text-[1.1rem] text-zp-prose">
					{m.l7_law_p2_before()}<b class="text-zp-ink">{m.l7_law_p2_bold()}</b>{m.l7_law_p2_after()}
				</p>
				<a
					href={resolve(localizeHref('/anadir-barco'))}
					class="mt-6 inline-flex items-center gap-2 rounded-md border border-transparent bg-zp-navy px-6 py-3.5 text-[0.86rem] font-semibold text-zp-paper transition-all hover:-translate-y-px hover:bg-zp-ink"
				>
					{m.landing_cta_register()}
					<ArrowIcon />
				</a>
			</div>
		</div>
	</div>
</section>

<section class="bg-zp-paper2 py-20 md:py-[100px]">
	<div class="mx-auto max-w-[1240px] px-5 md:px-10">
		<div class="mb-14 grid items-start gap-9 lg:grid-cols-[0.9fr_1.1fr] lg:gap-[70px]">
			<div>
				<span
					class="mb-5 block text-[0.72rem] font-semibold tracking-[0.22em] text-zp-muted uppercase"
					>{m.l7_how_eyebrow()}</span
				>
				<h2 class="text-[clamp(1.9rem,3.4vw,3rem)] leading-[1.06]">
					{m.l7_how_h2()}
				</h2>
			</div>
			<p class="m-0 text-[1.1rem] text-zp-prose">
				{m.l7_how_intro()}
			</p>
		</div>
		<div class="grid gap-7 md:grid-cols-3">
			{#each features as f (f.n)}
				<div class="rounded-2xl border border-zp-line bg-white px-8 py-[34px]">
					<div class="mb-8 font-serif text-[1.05rem] text-zp-accent">{f.n}</div>
					<h3 class="mb-3 text-[1.3rem] leading-snug">{f.title}</h3>
					<p class="m-0 text-[0.98rem] text-zp-muted">{f.body}</p>
				</div>
			{/each}
		</div>
	</div>
</section>

<section class="py-20 md:py-[100px]">
	<div class="mx-auto max-w-[1240px] px-5 md:px-10">
		<div class="grid items-start gap-9 lg:grid-cols-[0.9fr_1.1fr] lg:gap-[70px]">
			<div>
				<span
					class="mb-5 block text-[0.72rem] font-semibold tracking-[0.22em] text-zp-muted uppercase"
					>{m.l7_price_eyebrow()}</span
				>
				<h2 class="text-[clamp(1.9rem,3.4vw,3rem)] leading-[1.06]">
					{m.l7_price_h2()}
				</h2>
			</div>
			<div>
				<p class="mb-5 text-[1.1rem] text-zp-prose">
					{m.l7_price_p1()}
				</p>
				<p class="mb-0 text-[1.1rem] text-zp-prose">
					{m.l7_price_p2_before()}<b class="text-zp-ink">{m.l7_price_p2_bold()}</b
					>{m.l7_price_p2_after()}
				</p>
				<a
					href={`${localizeHref('/')}#tarifas`}
					class="mt-2 inline-flex items-center rounded-[40px] border border-zp-line px-6 py-3.5 text-[0.86rem] font-semibold text-zp-ink transition-colors hover:border-zp-ink"
				>
					{m.l7_price_link()}
				</a>
			</div>
		</div>
	</div>
</section>

<section id="registro" class="bg-zp-paper2 py-20 md:py-[100px]">
	<div class="mx-auto max-w-[1240px] px-5 md:px-10">
		<div
			class="grid items-center gap-9 rounded-3xl bg-zp-navy p-8 text-zp-paper md:grid-cols-[1fr_1fr] md:gap-[60px] md:p-14"
		>
			<div>
				<span
					class="mb-4 block text-[0.72rem] font-semibold tracking-[0.22em] text-zp-paper/60 uppercase"
					>{m.l7_cta_eyebrow()}</span
				>
				<h2 class="text-[clamp(1.8rem,3vw,2.6rem)] leading-[1.06] text-white">
					{m.l7_cta_h2()}
				</h2>
				<p class="mt-4 text-[1.02rem] text-zp-paper/78">
					{m.l7_cta_p()}
				</p>
			</div>
			<div class="flex flex-col gap-3 sm:gap-4">
				<a
					href={resolve(localizeHref('/anadir-barco') as '/anadir-barco')}
					class="inline-flex w-full items-center justify-center gap-2 rounded-[40px] border border-transparent bg-zp-paper px-6 py-3.5 text-[0.86rem] font-semibold text-zp-navy transition-all hover:-translate-y-px hover:bg-white"
				>
					{m.landing_cta_register()}
					<ArrowIcon />
				</a>
				<a
					href={`tel:${supportPhone}`}
					class="inline-flex w-full flex-col items-center justify-center gap-0.5 rounded-[40px] border border-white/20 bg-white/5 px-6 py-3.5 text-center transition-all hover:-translate-y-px hover:border-white/40 hover:bg-white/10 sm:flex-row sm:gap-3 sm:py-3.5"
				>
					<span class="inline-flex items-center gap-2 text-[0.86rem] font-semibold text-zp-paper">
						<Phone class="size-4 shrink-0" aria-hidden="true" />
						{m.l7_phone_label()}
					</span>
					<span class="text-[0.82rem] text-zp-paper/65 sm:border-l sm:border-white/20 sm:pl-3">
						{supportPhoneDisplay}
					</span>
				</a>
				<p class="text-center text-[0.78rem] text-zp-paper/50 sm:text-left">
					{m.l7_hours()}
				</p>
			</div>
		</div>
	</div>
</section>
